import { GlobalWizardState, LOBState } from "stores/wizard/state";
import { observable, action, computed } from "mobx";
import { CarConfig } from "stores/wizard/config";
import { TypeaheadSelection } from "uitk-react-typeahead";
import * as React from "react";
import { setCookie } from "components/utility/cookie";
import { CarLastSearchData, LastSearchData } from "stores/wizard/LastSearchData";
import { Travelers, Room } from "src/components/shared/TravelersField/typings";
import { travelersMetadata } from "src/components/shared/TravelersField/utils";
import { DateState } from "../global/date/typings";
import { TypeaheadSelectionProps } from "src/components/flexComponents/WizardHotelPWA/typings";
import { locKeys } from "src/components/flexComponents/WizardCarPWA/components/l10n";
import { carSubLOBs } from "../typings";
import { GroundTransportationLocationState } from "./GroundTransportationLocationState";
import { locationState } from "../global/location/locationState";

export class CarWizardState implements LOBState {
  private globalState: GlobalWizardState;

  @computed
  public get location() {
    return this.globalState.location;
  }

  @computed
  public get canTrack() {
    return this.globalState.canTrack;
  }

  public get isDesktop() {
    return this.globalState.isDesktop;
  }

  @computed
  public get travelersValueChanged() {
    return this.globalState.travelersValueChanged;
  }

  @action
  public setTravelersValue = () => {
    this.globalState.setTravelersValue();
  };

  public lastSearchData?: CarLastSearchData;

  @computed
  public get useLastSearchForMultiLOB() {
    return this.globalState.config.useLastSearchForMultiLOB;
  }

  public get useLastSearch() {
    return (this.useLastSearchForMultiLOB || this.config.lastSearch.useLastSearchForSingleLOB) && this.isDesktop;
  }

  @observable
  public config: CarConfig;

  @observable
  public isDifferentDropOff: boolean = false;

  public toggleIsDifferentDropOff() {
    const { pickUpLocationLabel, pickUpDropOffLabel } = locKeys;
    this.isDifferentDropOff = !this.isDifferentDropOff;
    this.config.location.origin.labelToken = this.isDifferentDropOff ? pickUpLocationLabel : pickUpDropOffLabel;
  }

  public setIsDifferentDropoff(differentDropoff: boolean) {
    const { pickUpLocationLabel, pickUpDropOffLabel } = locKeys;
    this.isDifferentDropOff = differentDropoff;
    this.config.location.origin.labelToken = this.isDifferentDropOff ? pickUpLocationLabel : pickUpDropOffLabel;
  }

  @observable
  public preferredBrand: string = "";

  @observable
  public pickupTime: string;

  private pickupTimeCannonicalMinutesDefault = 630;
  public pickupTimeCannonicalMinutes: number = this.pickupTimeCannonicalMinutesDefault; //"1030AM"

  @observable
  public returnTime: string;

  @action
  public setDefaultShowingTime = () => {
    if (!this.pickupTime) {
      this.pickupTime = this.config.defaultShowingTime;
    }
    if (!this.returnTime) {
      this.returnTime = this.config.defaultShowingTime;
    }
  };

  @observable
  public subLOBState: carSubLOBs = carSubLOBs.rental;

  @action
  public updateSubLOBState(subLOBState: carSubLOBs) {
    this.subLOBState = subLOBState;
  }

  public wizardInputsArray: React.RefObject<HTMLInputElement>[] = [];

  public driversAgeInputRef: React.RefObject<HTMLInputElement>;

  @observable
  public carCouponCodeType: string = "noCode";

  @observable
  public couponCode0: string;

  @observable
  public couponCode1: string;

  @observable
  public couponCode2: string;

  @observable
  public driversAge = "30";

  @observable
  public errorInputRef: React.RefObject<HTMLHeadingElement> | null;

  @observable
  public errorSummaryRef: React.RefObject<HTMLInputElement> | null;

  @observable
  public isCarFormValid = true;

  @observable
  public isGroundTransportFormValid = true;

  @observable
  public numberOfErrors = 0;

  @computed
  public get date() {
    return this.globalState.date;
  }

  // Ground Transportation
  @observable
  public additionalSingleDate: DateState;

  @observable
  public roundTrip: boolean = false;

  @observable
  public nonHotelTravelersInvalidKey: string | "";

  public toggleBookRoundtrip() {
    this.roundTrip = !this.roundTrip;
  }

  @action
  public updateAdditionalSingleDate = (start: Date, end: Date) => {
    this.additionalSingleDate.start = start;
    this.additionalSingleDate.end = end;
  };

  @action
  public updateNonHotelTravelersSelection = (travelers: Travelers) => {
    this.globalState.updateNonHotelTravelersSelection(travelers);
    this.nonHotelTravelersInvalidKey = "";
  };

  @action findFocusElement = (keyword: string) =>
    this.wizardInputsArray.find((element) => element.current !== null && element.current.outerHTML.includes(keyword));

  @action
  public validateLessThanNTravelers = () => {
    if (!this.globalState.validateLessThanNTravelers(this.nonHotelTravelersMetadata, this.config.travelers)) {
      this.nonHotelTravelersInvalidKey = this.config.travelers.invalidLessThanNTravelersMessageToken;

      return false;
    }

    return true;
  };

  @action
  public validateTravelersField = () => this.validateLessThanNTravelers();

  @computed
  public get travelers() {
    const nonHotelRoom: [Room] = [
      {
        adults: 2,
        children: [],
        infants: [],
      },
    ];

    const applicableTravelerState = this.globalState.travelers.nonHotel;
    const applicableRoomsForTravelers: Room[] = applicableTravelerState.rooms.map((room: Room) => {
      const applicableAdultsPerRoom =
        room.adults > this.config.travelers.maxAdultsPerRoom ? this.config.travelers.maxAdultsPerRoom : room.adults;

      const applicableChildrenPerRoom =
        room.children.length > this.config.travelers.maxChildrenPerRoom
          ? room.children.splice(0, this.config.travelers.maxChildrenPerRoom)
          : room.children;

      const applicableInfantsPerRoom = this.config.travelers.withInfants
        ? room.infants.length > this.config.travelers.maxInfantsPerRoom
          ? room.infants.splice(0, this.config.travelers.maxInfantsPerRoom)
          : room.infants
        : [];

      return {
        adults: applicableAdultsPerRoom,
        children: applicableChildrenPerRoom,
        infants: applicableInfantsPerRoom,
      };
    });

    nonHotelRoom[0] = applicableRoomsForTravelers[0];

    const gtTravelers = {
      hotel: {
        rooms: applicableRoomsForTravelers,
        infantSeating: this.globalState.travelers.hotel.infantSeating,
      },
      hotelPackage: {
        rooms: applicableRoomsForTravelers,
        infantSeating: this.globalState.travelers.hotelPackage.infantSeating,
      },
      nonHotel: {
        rooms: nonHotelRoom,
        infantSeating: this.globalState.travelers.nonHotel.infantSeating,
      },
      cruise: {
        rooms: nonHotelRoom,
        infantSeating: this.globalState.travelers.nonHotel.infantSeating,
      },
    };

    return gtTravelers;
  }

  @computed
  public get nonHotelTravelersMetadata() {
    return travelersMetadata(this.travelers.nonHotel);
  }

  @computed
  public get cruiseTravelersMetadata() {
    return travelersMetadata(this.travelers.cruise);
  }

  //validations
  @observable
  public originInvalidKey: string | "";

  @observable
  public destinationInvalidKey: string | "";

  @observable
  public driversAgeInvalidKey: string | "";

  @action moreThanOneError = () => {
    return this.globalState.moreThanOneError(this.numberOfErrors);
  };

  @action
  public validateOriginField = () => {
    const isGroundTransportForm = this.subLOBState === carSubLOBs.groundTransportation;

    if (!this.globalState.validateOriginField() && !isGroundTransportForm) {
      this.originInvalidKey = this.config.location.origin.invalidLocationMessageKey;
      this.errorInputRef = this.findFocusElement("location")!;

      return false;
    }

    if (!this.groundTransportationLocationState.validateOriginField() && isGroundTransportForm) {
      this.groundTransportationLocationState.originInvalidKey = this.groundTransportationLocationState.config.location.origin!.invalidLocationMessageKey;

      this.errorInputRef = this.groundTransportationLocationState.isOriginAirport
        ? this.findFocusElement("location")!
        : this.findFocusElement("hotel")!;

      return false;
    }

    this.originInvalidKey = "";

    return true;
  };

  public findElementRef = (wizardInputsArray: React.RefObject<HTMLInputElement>[], elementName: string) =>
    this.wizardInputsArray.find(
      (element) => element.current !== null && element.current.outerHTML.includes(elementName)
    );

  @action
  public updateDestinationSelectionSameAsOrigin() {
    const { destination, origin } = this.location;
    destination.value = origin.value;
    destination.metaData.regionId = origin.metaData.regionId;
    destination.metaData.ttla = origin.metaData.ttla;
    destination.metaData.countryCode = origin.metaData.countryCode;
    destination.metaData.shortName = origin.metaData.shortName;
  }

  @action
  public validateDestinationField = () => {
    const isGroundTransportForm = this.subLOBState === carSubLOBs.groundTransportation;
    const formHasDestinationField = this.isDifferentDropOff || isGroundTransportForm;

    if (formHasDestinationField) {
      if (!this.globalState.validateDestinationField() && !isGroundTransportForm) {
        if (this.globalState.validateOriginField() && this.isDesktop) {
          this.updateDestinationSelectionSameAsOrigin();

          return true;
        }
        this.destinationInvalidKey = this.config.location.destination.invalidLocationMessageKey;

        if (!this.errorInputRef) {
          this.errorInputRef = this.findFocusElement("location")!;
        }

        return false;
      } else if (!this.groundTransportationLocationState.validateDestinationField() && isGroundTransportForm) {
        this.groundTransportationLocationState.destinationInvalidKey = this.groundTransportationLocationState.config.location.destination!.invalidLocationMessageKey;

        if (!this.errorInputRef) {
          this.errorInputRef = this.groundTransportationLocationState.isOriginAirport
            ? this.findFocusElement("hotel")!
            : this.findFocusElement("location")!;
        }

        return false;
      }

      return true;
    }

    return true;
  };

  @observable
  public dateStartInvalidKey: string = "";

  @observable
  public dateEndInvalidKey: string = "";

  @observable
  public timeStartInvalidKey: string = "";

  @action
  public validateMaxDateRange = () => {
    const { start, end } = this.date;
    const { maxDaysRange: gtMaxDaysRange, invalidMaxDatesRangeMessageToken } = this.config.gtConfig.date;
    const { maxDaysRange: carMaxDaysRange } = this.config.date;

    if (
      this.subLOBState === carSubLOBs.groundTransportation &&
      !this.globalState.validateMaxDateRange(start, end, gtMaxDaysRange)
    ) {
      this.dateEndInvalidKey = invalidMaxDatesRangeMessageToken;
      if (!this.errorInputRef) {
        this.errorInputRef = this.findFocusElement("d2")!;
      }

      return false;
    }

    if (!this.globalState.validateMaxDateRange(start, end, carMaxDaysRange)) {
      this.dateEndInvalidKey = invalidMaxDatesRangeMessageToken;
      if (!this.errorInputRef) {
        this.errorInputRef = this.findFocusElement("d2")!;
      }

      return false;
    }

    return true;
  };

  @action
  public validateStartTime = () => {
    const isToday = (dt: Date) => {
      const today = new Date();

      return (
        dt.getDate() === today.getDate() &&
        dt.getMonth() === today.getMonth() &&
        dt.getFullYear() === today.getFullYear()
      );
    };

    const isSelectedHourValid = () => {
      const today = new Date();
      const now = today.getHours() * 60 + today.getMinutes();

      return this.pickupTimeCannonicalMinutes >= now;
    };

    const isCarTabSelected = this.subLOBState === carSubLOBs.rental;

    if (isToday(this.date.start) && !isSelectedHourValid()) {
      this.timeStartInvalidKey = isCarTabSelected
        ? this.config.date.invalidStartTimeMessageToken!
        : this.config.gtConfig.date.invalidStartTimeMessageToken!;

      if (!this.errorInputRef) {
        this.errorInputRef = this.findFocusElement("d2")!;
      }

      return false;
    }

    return true;
  };

  @action
  public validateStartTimeField = () => this.validateStartTime();

  @action
  public validateEndDateField = () => this.validateMaxDateRange();

  @action
  public validateDriversAgeField = () => {
    if (!this.driversAge) {
      this.driversAgeInvalidKey = this.config.invalidDriversAgeMessageToken;
      if (!this.errorInputRef) {
        this.errorInputRef = this.findFocusElement("age")!;
      }

      return false;
    }

    return true;
  };

  @action
  public resetValidations = () => {
    this.originInvalidKey = "";
    this.destinationInvalidKey = "";
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
    this.timeStartInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";
    this.driversAgeInvalidKey = "";
  };

  public validateForm = () => {
    this.cleanErrorState();

    const validations: boolean[] = [
      this.validateOriginField(),
      this.validateDestinationField(),
      this.validateEndDateField(),
      this.validateStartTimeField(),
      this.validateDriversAgeField(),
    ];

    if (this.subLOBState === carSubLOBs.groundTransportation) {
      validations.push(this.validateTravelersField());
    }
    validations.forEach((value) => !value && (this.numberOfErrors += 1));

    return !validations.includes(false);
  };

  // other actions
  @action
  public overrideConfig(callback: () => void): void {
    callback();
  }

  @action
  public updateDateSelection = (start: Date, end: Date) => {
    this.globalState.updateDateSelection(start, end);
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
    this.timeStartInvalidKey = "";
  };

  @action
  public updateDestinationSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateDestinationSelection(selection);
    this.destinationInvalidKey = "";
  };

  @action
  public updateCouponCodeType = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
    const carCouponCodeType = selectEvent.target.value;
    this.carCouponCodeType = carCouponCodeType;
    this.couponCode0 = "";
    this.couponCode1 = "";
    this.couponCode2 = "";
  };

  @action
  public updateCouponCode = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const code = event.target.value;
    if (index === 0) {
      this.couponCode0 = code;
    } else if (index === 1) {
      this.couponCode1 = code;
    } else if (index === 2) {
      this.couponCode2 = code;
    }
  };

  @action
  public updateDriversAgeInput = (event: React.ChangeEvent<HTMLInputElement>) => (this.driversAge = event.target.value);

  /* istanbul ignore next */
  public updateHotelRegionId = (regionId: number) => {
    // @ts-ignore
    this.config.gtConfig.location?.destination?.essAdapterConfig!.regionId = regionId;
  };

  @action
  public updateOriginSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateOriginSelection(selection);
    if (this.subLOBState === carSubLOBs.groundTransportation) {
      const {
        data: { regionId },
      } = selection as TypeaheadSelectionProps;
      this.updateHotelRegionId(parseInt(regionId, 10));
      this.originInvalidKey = "";

      return;
    }
    this.originInvalidKey = "";
  };

  @action
  public updateDestinationFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string,
    destinationID: string,
    hotelID: string
  ) {
    this.globalState.updateDestinationFromCookie(value, regionID, countryCode, latLong, ttla, destinationID, hotelID);
  }

  @action
  public updateOriginFromCookie(value: string, regionID: string, countryCode: string, latLong: string, ttla: string) {
    this.globalState.updateOriginFromCookie(value, regionID, countryCode, latLong, ttla);
    this.originInvalidKey = "";
  }

  @action
  public setPreferredBrand = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
    this.preferredBrand = selectEvent.target.value;
    this.couponCode0 = "";
    this.couponCode1 = "";
    this.couponCode2 = "";
  };

  @action
  public setPickupTime = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
    this.pickupTime = selectEvent.target.value;
    const cannonicalMinutes =
      selectEvent.target && selectEvent.target.selectedOptions
        ? selectEvent.target.selectedOptions[0].dataset["time"]
        : "";
    this.pickupTimeCannonicalMinutes = cannonicalMinutes
      ? parseInt(cannonicalMinutes, 10)
      : this.pickupTimeCannonicalMinutesDefault;
    this.timeStartInvalidKey = "";
  };

  @action
  public setReturnTime = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
    this.returnTime = selectEvent.target.value;
  };

  public setGroundTransportLocation = () => {
    const groundTransportOrigin = this.groundTransportationLocationState.location.origin;
    const groundTransportOriginMetaData = this.groundTransportationLocationState.location.origin.metaData;
    const groundTransportDestination = this.groundTransportationLocationState.location.destination;
    const groundTransportDestinationMetaData = this.groundTransportationLocationState.location.destination.metaData;

    return {
      origin: {
        value: groundTransportOrigin.value || "",
        regionID: groundTransportOriginMetaData.regionId || "",
        ttla: groundTransportOriginMetaData.ttla || "",
        countryCode: groundTransportOriginMetaData.countryCode || "",
        latLong: groundTransportOriginMetaData.latLong || "",
        destinationID: groundTransportOriginMetaData.latLong || "",
        hotelID: groundTransportOriginMetaData.hotelId || "",
      },
      destination: {
        value: groundTransportDestination.value || "",
        regionID: groundTransportDestinationMetaData.regionId || "",
        ttla: groundTransportDestinationMetaData.ttla || "",
        countryCode: groundTransportDestinationMetaData.countryCode || "",
        latLong: groundTransportDestinationMetaData.latLong || "",
        destinationID: groundTransportOriginMetaData.destinationId || "",
        hotelID: groundTransportOriginMetaData.hotelId || "",
      },
    };
  };

  public setLastSearchCookie = () => {
    if (this.canTrack && this.useLastSearch) {
      const origin = this.location.origin;
      const originMetaData = this.location.origin.metaData;
      const destination = this.location.destination;
      const destinationMetaData = this.location.destination.metaData;
      const date = this.date;
      const couponCode0 = this.couponCode0;
      const couponCode1 = this.couponCode1;
      const couponCode2 = this.couponCode2;

      const lastSearchCookieObject: LastSearchData = {
        lob: this.config.lastSearch.lobKey,
        car: {
          origin: {
            value: origin.value || "",
            regionID: originMetaData.regionId || "",
            ttla: originMetaData.ttla || "",
            countryCode: originMetaData.countryCode || "",
            latLong: originMetaData.latLong || "",
            destinationID: originMetaData.destinationId || "",
            hotelID: originMetaData.hotelId || "",
          },
          destination: {
            value: destination.value || "",
            regionID: destinationMetaData.regionId || "",
            ttla: destinationMetaData.ttla || "",
            countryCode: destinationMetaData.countryCode || "",
            latLong: destinationMetaData.latLong || "",
            destinationID: destinationMetaData.destinationId || "",
            hotelID: destinationMetaData.hotelId || "",
          },
          groundTransport: {
            location: this.setGroundTransportLocation(),
            isOriginAirport: this.groundTransportationLocationState.isOriginAirport,
          },
          date: {
            pickup: date.start ? date.start.toString() : "",
            dropoff: date.end ? date.end.toString() : "",
          },
          time: {
            pickup: this.pickupTime,
            return: this.returnTime,
          },
          isDifferentDropoff: this.isDifferentDropOff.toString(),
          preferred_brand: this.preferredBrand,
          coupon_code: {
            type: this.carCouponCodeType,
            0: couponCode0 || "",
            1: couponCode1 || "",
            2: couponCode2 || "",
          },
        },
      };

      if (this.isDifferentDropOff) {
        lastSearchCookieObject.car!.destination.value = destination.value || "";
        lastSearchCookieObject.car!.destination.regionID = destinationMetaData.regionId || "";
        lastSearchCookieObject.car!.destination.ttla = destinationMetaData.ttla || "";
        lastSearchCookieObject.car!.destination.countryCode = destinationMetaData.countryCode || "";
        lastSearchCookieObject.car!.destination.latLong = destinationMetaData.latLong || "";
      }

      setCookie({
        key: this.config.lastSearch.lastSearchKey,
        value: JSON.stringify(lastSearchCookieObject),
      });
    }
  };

  public updateDateFromConfig = () => {
    this.globalState.updateDateFromConfig(this.config.date);
  };

  public updateStateFromCookie = () => {
    const lastSearchData = this.lastSearchData;
    this.setDefaultShowingTime();

    if (this.canTrack && this.useLastSearch && lastSearchData) {
      const originValue = decodeURIComponent(lastSearchData.origin?.value);
      const destinationValue = decodeURIComponent(lastSearchData.destination?.value);
      const startDateFromCookie = lastSearchData.date?.pickup ? new Date(lastSearchData.date.pickup) : new Date();
      const endDateFromCookie = lastSearchData.date?.dropoff ? new Date(lastSearchData.date.dropoff) : new Date();

      const isDifferentDropoff = lastSearchData.isDifferentDropoff
        ? JSON.parse(lastSearchData.isDifferentDropoff)
        : undefined;
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      const startDateToUse = startDateFromCookie >= today ? startDateFromCookie : today;
      const endDateToUse = endDateFromCookie >= tomorrow ? endDateFromCookie : tomorrow;

      if (originValue) {
        this.updateOriginFromCookie(
          originValue !== "undefined" ? originValue : "",
          lastSearchData.origin?.regionID ? lastSearchData.origin.regionID : "",
          lastSearchData.origin?.countryCode ? lastSearchData.origin.countryCode : "",
          lastSearchData.origin?.latLong ? lastSearchData.origin.latLong : "",
          lastSearchData.origin?.ttla ? lastSearchData.origin.ttla : ""
        );
      }

      if (isDifferentDropoff && destinationValue) {
        this.setIsDifferentDropoff(isDifferentDropoff);
        if (originValue !== "undefined" && destinationValue === "undefined") {
          lastSearchData.destination = {
            value: originValue,
            regionID: lastSearchData.origin.regionID,
            countryCode: lastSearchData.origin.countryCode,
            latLong: lastSearchData.origin.latLong,
            ttla: lastSearchData.origin.ttla,
            destinationID: lastSearchData.destination.destinationID,
            hotelID: lastSearchData.destination.hotelID,
          };
          this.updateDestinationFromCookie(
            originValue,
            lastSearchData.destination.regionID,
            lastSearchData.destination.countryCode,
            lastSearchData.destination.latLong,
            lastSearchData.destination.ttla,
            lastSearchData.destination.destinationID,
            lastSearchData.destination.hotelID
          );
        } else {
          this.updateDestinationFromCookie(
            destinationValue !== "undefined" ? destinationValue : "",
            lastSearchData.destination?.regionID ? lastSearchData.destination.regionID : "",
            lastSearchData.destination?.countryCode ? lastSearchData.destination.countryCode : "",
            lastSearchData.destination?.latLong ? lastSearchData.destination.latLong : "",
            lastSearchData.destination?.ttla ? lastSearchData.destination.ttla : "",
            lastSearchData.destination?.destinationID ? lastSearchData.destination.destinationID : "",
            lastSearchData.destination?.hotelID ? lastSearchData.destination.hotelID : ""
          );
        }
      }
      this.updateDateSelection(startDateToUse, endDateToUse);

      //populate from last search or from default if unavailable
      this.pickupTime = lastSearchData.time?.pickup ? lastSearchData.time.pickup : this.pickupTime;
      this.returnTime = lastSearchData.time?.return ? lastSearchData.time.return : this.returnTime;
      this.preferredBrand = lastSearchData.preferred_brand ? lastSearchData.preferred_brand : this.preferredBrand;
      this.carCouponCodeType = lastSearchData.coupon_code?.type
        ? lastSearchData.coupon_code.type
        : this.carCouponCodeType;
      this.couponCode0 = lastSearchData.coupon_code?.hasOwnProperty("0")
        ? lastSearchData.coupon_code["0"]
        : this.couponCode0;
      this.couponCode1 = lastSearchData.coupon_code?.hasOwnProperty("1")
        ? lastSearchData.coupon_code["1"]
        : this.couponCode1;
      this.couponCode2 = lastSearchData.coupon_code?.hasOwnProperty("2")
        ? lastSearchData.coupon_code["2"]
        : this.couponCode2;
    }
  };

  public submit = (event: React.FormEvent) => {
    const isValidForm = this.validateForm();
    if (!isValidForm) {
      event.preventDefault();
      this.updateInvalidFormsState(false);
      this.errorSummaryRef = this.findFocusElement("error")!;
      this.errorSummaryRef?.current?.focus();
    } else if (this.canTrack && this.useLastSearch) {
      this.setLastSearchCookie();
      this.updateInvalidFormsState(true);
    }
  };

  /**
   * Cleans error state in all forms
   */

  @action
  public cleanErrorState = () => {
    if (this.errorInputRef) {
      this.errorInputRef = null;
    }
    this.numberOfErrors = 0;
    this.isCarFormValid = true;
    this.isGroundTransportFormValid = true;
  };

  @action
  public resetSubLobState = () => {
    this.updateSubLOBState(carSubLOBs.rental);
  };

  @action
  public resetSubLobTabs = () => {
    this.updateSubLOBState(carSubLOBs.rental);
  };

  //Resets other cars forms to not have errors when you change to a different car sublob type
  //Shows error message in the right cars type
  @action
  public updateInvalidFormsState = (validState?: boolean) => {
    const state = validState !== undefined ? validState : true;
    switch (this.subLOBState) {
      case carSubLOBs.groundTransportation:
        this.isGroundTransportFormValid = state;
        this.isCarFormValid = true;
        break;
      default:
        this.isCarFormValid = state;
        this.isGroundTransportFormValid = true;
        break;
    }
  };

  //Gt form v2

  @observable
  public groundTransportationLocationState: GroundTransportationLocationState;

  constructor(config: CarConfig, globalState: GlobalWizardState, lastSearchData?: LastSearchData) {
    this.config = config;
    this.globalState = globalState;
    this.groundTransportationLocationState = new GroundTransportationLocationState(
      config.gtConfig,
      locationState(),
      lastSearchData?.car
    );
    this.additionalSingleDate = this.globalState.getDateFromConfig(config.date);
    this.setDefaultShowingTime();
    if (lastSearchData && this.canTrack) {
      this.lastSearchData = lastSearchData.car;
    }
  }
}
